// Primero que todo: mira la entrada de Platform Admin desde el arranque (ver vigiaDeEntrada.js).
import './vigiaDeEntrada.js';
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { msalInstance } from './auth.js';
import { AuthGate } from './AuthGate.jsx';
import App from '../deskte-full.jsx';
import { ErrorBoundary } from './ErrorBoundary.jsx';
import { AvisoDeServidor } from './components/AvisoDeServidor.jsx';
import { ToastViewport } from './components/Toast.jsx';
import { installFrontendSentry } from './sentry.js';
import { installDomGuard } from './domGuard.js';
// La rueda del raton deja de cambiar los campos de numero (Lissette 2026-09-04).
import { evitarRuedaEnNumeros } from './sinRuedaEnNumeros.js';
import { installPwaInstallButton, isPublicRecipientPage } from './pwa-install.js';
import { IS_DEMO, IS_DEMO_BUILD } from './demoMode.js';
import { installDemoChrome } from './demoChrome.js';
// F-N7 (audit 2026-05-22): the previous inline `<style>{CSS_STR}</style>`
// blocks in deskte-full.jsx required `style-src 'unsafe-inline'` in the
// SPA's CSP. The CSS is now a real bundle asset imported here — Vite
// emits it as a single .css file that the browser fetches via the
// `style-src 'self'` rule, no inline blocks needed.
import './styles/dkt.css';

// Initialize MSAL once before mounting React.
// handleRedirectPromise must run on every page load to complete redirect flows.
async function bootstrap() {
  // Guard against browser extensions (Google Translate et al.) that detach
  // DOM nodes React still tracks, which otherwise crash the whole tree with
  // "NotFoundError: Failed to execute 'removeChild'". Must run before React
  // mounts. See domGuard.js for the full rationale.
  installDomGuard();

  // Un `<input type="number">` con el foco cambia de valor al rodar la rueda
  // encima: quien acaba de escribir una prima y rueda para seguir bajando se
  // lleva otro numero guardado sin haberlo tecleado. Se engancha una sola vez
  // para toda la aplicacion; hacerlo campo por campo (66 y subiendo) seria
  // asegurarse de que al proximo se le olvida.
  evitarRuedaEnNumeros(document);

  // Stripe bounces back to /billing/success?session_id=... or /billing/cancel
  // (success_url / cancel_url in billing.routes.ts). SWA's navigationFallback
  // serves index.html for those paths so the app boots fine, but the URL then
  // sticks — it lands in bookmarks and in the PWA's start URL, and the stray
  // session_id shows up in shared links. Nothing routes off these paths, so
  // swap them for "/" before React mounts.
  if (window.location.pathname.startsWith('/billing/')) {
    try { window.history.replaceState({}, '', '/'); } catch { /* non-fatal */ }
  }

  // Install Sentry handlers BEFORE MSAL init so any error from MSAL itself
  // is captured. No-op if VITE_SENTRY_DSN is unset.
  installFrontendSentry();

  // Public demo build (VITE_DEMO=1): there is no identity and no backend, so
  // MSAL is skipped entirely — no initialize(), no MsalProvider. AuthGate is
  // the only consumer of the MSAL hooks and it is not rendered here, and
  // leaving _instance unset is what makes apiFetch refuse to reach the
  // network (see the IS_DEMO guard in AuthGate.jsx#apiFetch).
  if (IS_DEMO_BUILD) {
    installDemoChrome();
    createRoot(document.getElementById('root')).render(
      <ErrorBoundary>
        <App />
        <ToastViewport />
      </ErrorBoundary>,
    );
    return;
  }

  await msalInstance.initialize();

  // Set active account on success events (login, token refresh)
  msalInstance.addEventCallback((event) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload?.account
    ) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });

  // Note: do NOT call handleRedirectPromise() here — MsalProvider does it
  // internally and exposes the result via the `inProgress` state. Calling it
  // twice consumes the redirect response and leaves the provider stuck in
  // the Startup interaction state.

  // Dev `?demo=1`: mount the app WITHOUT AuthGate so it runs on the
  // fictional SEED_* dataset (see _demoMode in deskte-full.jsx). Used to
  // capture help-centre screenshots and tutorial video without putting real
  // client PHI in material every agency can see. IS_DEMO folds in
  // import.meta.env.DEV, so this branch is dead-code-eliminated from the
  // production bundle — app.deskte.com can never be opened this way.
  const demoMode = IS_DEMO;

  createRoot(document.getElementById('root')).render(
    <MsalProvider instance={msalInstance}>
      <ErrorBoundary>
        {demoMode ? <App /> : <AuthGate><App /></AuthGate>}
      </ErrorBoundary>
      {/* Fuera del ErrorBoundary: si lo que se cae es una pantalla, el aviso de
          "el servidor esta tardando" tiene que seguir pudiendo salir. */}
      <AvisoDeServidor />
      <ToastViewport />
    </MsalProvider>,
  );
}

bootstrap().catch((err) => {
  console.error('[bootstrap] MSAL init failed:', err);
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `<pre style="color:#f87171;padding:2rem;font-family:monospace;">Init failed: ${err.message}</pre>`;
  }
});

// PWA: register the service worker so Deskte is installable as a desktop/mobile
// app, and show an "Instalar app" affordance when the browser reports it's
// installable. Only in production builds — in `vite dev` a SW fights HMR. The
// worker runs same-origin and bypasses all auth/API traffic (see public/sw.js).
// Public recipient pages (a client opening a /sign, /upload, /invoice, ... link)
// are one-time visits by someone who is NOT a Deskte user — don't register the
// CRM service worker there (it also keeps the page from being "installable", so
// the client is never prompted to install the app).
// !IS_DEMO_BUILD: the demo bundle is served from deskte.com/demo/app/, so
// registering the CRM worker would scope a cache over the marketing site's
// root and start serving stale shells there. The demo is disposable anyway —
// it has nothing to cache and nothing to update.
if (import.meta.env.PROD && !IS_DEMO_BUILD && 'serviceWorker' in navigator && !isPublicRecipientPage()) {
  // When a freshly-deployed SW takes control (it calls skipWaiting + claim),
  // reload ONCE so the page swaps to the new bundle automatically — without the
  // user having to clear cache or reopen the app (Lissette 2026-06-21/22:
  // deployed fixes weren't appearing because a STALE service worker kept
  // serving the old shell → old JS bundle). controllerchange only fires on an
  // actual SW takeover, so normal opens never reload; the guard prevents loops.
  let _swRefreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (_swRefreshing) return;
    _swRefreshing = true;
    window.location.reload();
  });

  // Nudge a newly-INSTALLED-but-waiting worker to take over immediately. The
  // current sw.js already self-calls skipWaiting() on install, but a user stuck
  // on an OLD worker (that did NOT) needs the page to ask the waiting one to
  // activate — otherwise the update sits idle until every tab closes. The SW
  // handles the 'SKIP_WAITING' message; activation → controllerchange → reload.
  const promote = (reg) => {
    if (reg && reg.waiting) reg.waiting.postMessage('SKIP_WAITING');
  };

  window.addEventListener('load', () => {
    navigator.serviceWorker
      // updateViaCache:'none' = the browser NEVER serves /sw.js from its HTTP
      // cache, so a redeployed worker is detected on the very next load instead
      // of being pinned by a cached script (the core reason updates "stuck").
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        promote(reg);                 // a waiting worker from a prior visit
        reg.update().catch(() => {}); // force an immediate freshness check
        // A new worker started installing on THIS load → promote it the moment
        // it finishes installing.
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed') promote(reg);
          });
        });
        // Long-lived sessions (the app stays open for hours): re-check for a
        // new deploy every 15 min so a fix lands without a manual reopen.
        setInterval(() => reg.update().catch(() => {}), 15 * 60 * 1000);
      })
      .catch((err) => console.warn('[pwa] SW registration failed:', err));
  });
}

// New-deploy detector for long-lived windows (audit 2026-07-31). The 15-min
// reg.update() above only notices a CHANGED /sw.js — and deploys almost never
// touch sw.js — so an installed PWA window left open for days kept running the
// OLD bundle until the user closed and reopened the app. Seats hit this
// constantly (the desktop app lives pinned in the taskbar): they were saving
// through week-old forms. Detect a new build by polling index.html (network,
// no-store) and comparing the hashed entry bundle to the one this page booted
// with. When they differ: a hidden window reloads silently (nothing to lose);
// a visible one gets a small banner and reloads on its next trip to the
// background, so we never yank a form out from under the user.
if (import.meta.env.PROD && !IS_DEMO_BUILD && !isPublicRecipientPage()) {
  const BUNDLE_RE = /src="(\/assets\/index-[^"]+\.js)"/;
  const currentBundle = (() => {
    try { return document.querySelector('script[src^="/assets/index-"]')?.getAttribute('src') || null; }
    catch { return null; }
  })();
  let newVersionPending = false;

  const showUpdateBanner = () => {
    if (document.getElementById('dkt-new-version-banner')) return;
    const bar = document.createElement('div');
    bar.id = 'dkt-new-version-banner';
    Object.assign(bar.style, {
      position: 'fixed', bottom: '16px', left: '50%', transform: 'translateX(-50%)',
      zIndex: '99999', background: '#0a558c', color: '#fff', padding: '10px 16px',
      borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,.35)',
      font: '14px system-ui, sans-serif', display: 'flex', gap: '12px', alignItems: 'center',
    });
    const txt = document.createElement('span');
    txt.textContent = 'Hay una versión nueva de Deskte · New version available';
    const btn = document.createElement('button');
    btn.textContent = 'Actualizar';
    Object.assign(btn.style, {
      background: '#fff', color: '#0a558c', border: 'none', borderRadius: '8px',
      padding: '6px 12px', font: 'inherit', fontWeight: '600', cursor: 'pointer',
    });
    btn.addEventListener('click', () => window.location.reload());
    bar.append(txt, btn);
    document.body.appendChild(bar);
  };

  const checkForNewBuild = async () => {
    if (!currentBundle || newVersionPending) return;
    try {
      const res = await fetch('/index.html', { cache: 'no-store' });
      if (!res.ok) return;
      const fresh = BUNDLE_RE.exec(await res.text())?.[1];
      if (!fresh || fresh === currentBundle) return;
      newVersionPending = true;
      if (document.visibilityState === 'hidden') { window.location.reload(); return; }
      showUpdateBanner();
    } catch { /* offline / transient — try again next tick */ }
  };
  setInterval(checkForNewBuild, 15 * 60 * 1000);
  document.addEventListener('visibilitychange', () => {
    // Pending update + user just left the tab → swap versions invisibly.
    if (newVersionPending && document.visibilityState === 'hidden') window.location.reload();
  });
}
// The demo is a sales page, not an app someone installs — and its manifest
// would point at the marketing origin. Skip the install affordance there.
if (!IS_DEMO_BUILD) installPwaInstallButton();
